import { promiseWithTimeout } from 'utils/promise-timeout-utils';
import * as Sentry from '@sentry/react';
import { loadPosthog, getPosthog } from 'utils/posthog-loader';
import { isProd, posthogHost, posthogKey } from 'constants/config';
import posthogStore from 'stores/mobx/posthog.store';
import BrowserStorage from 'services/BrowserStorage';

const SEGMENT_READY_TIMEOUT = 1000;
const GA_ATTRIBUTION_KEY = 'gaAttribution';
// the formats gtag('get', ...) returns: client_id is "<random>.<timestamp>", session_id is a timestamp
const GA_CLIENT_ID_PATTERN = /^\d+\.\d+$/;
const GA_SESSION_ID_PATTERN = /^\d+$/;

export type GaAttribution = { gaClientId: string; gaSessionId?: string };

export enum PosthogGroup {
  COMPANY = 'company'
}

declare global {
  interface Window {
    analytics: SegmentAnalytics.AnalyticsJS;
  }
}

export interface TrackingData {
  eventName: string;
  eventProperties?: any;
}

export enum EventNames {
  VCS_INTEGRATION_ATTEMPT = 'VCS_INTEGRATION_ATTEMPT',
  VCS_INTEGRATION_FAILURE = 'VCS_INTEGRATION_FAILURE',
  VCS_INTEGRATION_SUCCESS = 'VCS_INTEGRATION_SUCCESS',
  TOP_BAR_SCHEDULE_DEMO = 'TOP_BAR_SCHEDULE_DEMO',
  TOP_BAR_DOCS_CLICK = 'TOP_BAR_DOCS_CLICK',
  MOBILE_BLOCKER = 'MOBILE_BLOCKER',
  SIGNUP = 'SIGN_UP_CLIENT_SIDE',
  SSO_ATTEMPT = 'SSO_ATTEMPT',
  TEMPLATE_WIZARD_SETTINGS_STEP = 'TEMPLATE_WIZARD_SETTINGS_STEP',
  TEMPLATE_WIZARD_VCS_STEP = 'TEMPLATE_WIZARD_VCS_STEP',
  TEMPLATE_WIZARD_CREDENTIALS_STEP = 'TEMPLATE_WIZARD_CREDENTIALS_STEP',
  TEMPLATE_WIZARD_VARIABLES_STEP = 'TEMPLATE_WIZARD_VARIABLES_STEP',
  TEMPLATE_WIZARD_PROJECTS_STEP = 'TEMPLATE_WIZARD_PROJECTS_STEP',
  TEMPLATE_WIZARD_MAPPING_STEP = 'TEMPLATE_WIZARD_MAPPING_STEP',
  TEMPLATE_WIZARD_ENVIRONMENT_DETAILS_STEP = 'TEMPLATE_WIZARD_ENVIRONMENT_DETAILS_STEP',
  TEMPLATE_WIZARD_DONE_STEP = 'TEMPLATE_WIZARD_DONE_STEP',
  TEMPLATE_WIZARD_SUBMIT = 'TEMPLATE_WIZARD_SUBMIT',
  SINGLE_USE_TEMPLATE_WIZARD_SUBMIT = 'SINGLE_USE_TEMPLATE_WIZARD_SUBMIT',
  CREATE_NEW_ENVIRONMENT_WITH_TEMPLATE_CLICK = 'CREATE_NEW_ENVIRONMENT_WITH_TEMPLATE_CLICK',
  CREATE_NEW_ENVIRONMENT_WITHOUT_TEMPLATE_CLICK = 'CREATE_NEW_ENVIRONMENT_WITHOUT_TEMPLATE_CLICK',
  ERROR_REPORTED = 'ERROR_REPORTED',
  CREATED_ENVIRONMENT = 'CREATED_ENVIRONMENT_CLIENT_SIDE',
  DEPLOYED_ENVIRONMENT = 'DEPLOYED_ENVIRONMENT_CLIENT_SIDE',
  PRETTY_PLAN_PRETTY_CLICK = 'PRETTY_PLAN_PRETTY_CLICK',
  PRETTY_PLAN_TEXT_CLICK = 'PRETTY_PLAN_TEXT_CLICK',
  PRETTY_PLAN_SEARCH_BAR_CLICK = 'PRETTY_PLAN_SEARCH_BAR_CLICK',
  PRETTY_PLAN_CHANGE_BLOCK_VIEW_CLICK = 'PRETTY_PLAN_RESOURCE_VIEW_CLICK',
  PRETTY_PLAN_COPY_CHANGE_VALUE_CLICK = 'PRETTY_PLAN_COPY_CHANGE_VALUE_CLICK',
  PRETTY_PLAN_HIDE_METADATA_CHANGES_CLICK = 'PRETTY_PLAN_HIDE_METADATA_CHANGES_CLICK',
  IN_APP_WHATS_NEW_SHOW_NEW_ITEMS = 'IN_APP_WHATS_NEW_SHOW_NEW_ITEMS',
  IN_APP_WHATS_NEW_ICON_CLICKED = 'IN_APP_WHATS_NEW_ICON_CLICKED',
  IN_APP_WHATS_NEW_VIEW_ITEM_CLICKED = 'IN_APP_WHATS_NEW_VIEW_ITEM_CLICKED',
  IN_APP_WHATS_NEW_VIEW_ALL_CLICKED = 'IN_APP_WHATS_NEW_VIEW_ALL_CLICKED',
  IN_APP_WHATS_NEW_RSS_FEED_CLICKED = 'IN_APP_WHATS_NEW_RSS_FEED_CLICKED',
  CMD_K_ITEM_CLICKED = 'CMD_K_ITEM_CLICKED',
  CMD_K_OPENED = 'CMD_K_OPENED',
  ORGANIZATION_CREATED = 'ORGANIZATION_CREATED',
  ONBOARDING_STARTED = 'ONBOARDING_STARTED',
  ONBOARDING_COMPLETED = 'ONBOARDING_COMPLETED',
  ONBOARDING_EXIT_ON_STEP = 'ONBOARDING_EXIT_ON_STEP',
  ONBOARDING_CREDENTIALS_COMPLETED = `ONBOARDING_CREDENTIALS_COMPLETED`,
  ONBOARDING_CREDENTIALS_SKIPPED = `ONBOARDING_CREDENTIALS_SKIPPED`,
  ONBOARDING_ENV_CREATED = `ONBOARDING_ENV_CREATED`, // TODO: https://trello.com/c/9KnZrxxF/6254-create-vs-import-environment
  ONBOARDING_ENV_IMPORTED = `ONBOARDING_ENV_IMPORTED`,
  ALL_QUEUED_DEPLOYMENTS_CANCELED = `ALL_QUEUED_DEPLOYMENTS_CANCELED`,
  CREDENTIAL_UPDATED = 'CREDENTIAL_UPDATED',
  TIER_LIMIT_REACHED_FE = 'TIER_LIMIT_REACHED_FE',
  GAME_START = 'GAME_START',
  STATE_VERSION_DOWNLOAD = 'STATE_VERSION_DOWNLOAD',
  STATE_VERSION_OPEN_DEPLOYMENT_LOG = 'STATE_VERSION_OPEN_DEPLOYMENT_LOG',
  ENVIRONMENT_LOCK_UPDATED = 'ENVIRONMENT_LOCK_UPDATED',
  PRO_TIER_MANAGE_SUBSCRIPTION = 'PRO_TIER_MANAGE_SUBSCRIPTION',
  BUSINESS_TIER_MANAGE_SUBSCRIPTION = 'BUSINESS_TIER_MANAGE_SUBSCRIPTION',
  CONTACT_US_BASIC_TIER_CLICK = 'CONTACT_US_BASIC_TIER_CLICK',
  CONTACT_US_BUSINESS_TIER_CLICK = 'CONTACT_US_BUSINESS_TIER_CLICK',
  CONTACT_US_PAY_GATE_CLICK = 'CONTACT_US_PAY_GATE_CLICK',
  UPGRADE_TO_PRO_TIER_CLICK = 'UPGRADE_TO_PRO_TIER_CLICK',
  SAVE_AS_TEMPLATE = 'SAVE_AS_TEMPLATE',
  RUN_MASS_IMPORT = 'RUN_MASS_IMPORT',
  HOMEPAGE_NAVIGATE_FROM_INSIGHTS = 'HOMEPAGE_NAVIGATE_FROM_INSIGHTS',
  HOMEPAGE_NAVIGATE_FROM_RECENT_TO_ALL_PROJECT = 'HOMEPAGE_NAVIGATE_FROM_RECENT_TO_ALL_PROJECT',
  HOMEPAGE_NAVIGATE_FROM_RECENT_TO_SPECIFIC_PROJECT = 'HOMEPAGE_NAVIGATE_FROM_RECENT_TO_SPECIFIC_PROJECT',
  AI_ANALYZER_FEEDBACK_POSITIVE = 'AI_ANALYZER_FEEDBACK_POSITIVE',
  AI_ANALYZER_FEEDBACK_NEGATIVE = 'AI_ANALYZER_FEEDBACK_NEGATIVE',
  DRIFT_ANALYSIS_FEEDBACK_POSITIVE = 'DRIFT_ANALYSIS_FEEDBACK_POSITIVE',
  DRIFT_ANALYSIS_FEEDBACK_NEGATIVE = 'DRIFT_ANALYSIS_FEEDBACK_NEGATIVE',
  DRIFT_ANALYSIS_FOUND_CAUSES = 'DRIFT_ANALYSIS_FOUND_CAUSES',
  DRIFT_ANALYSIS_CAUSES_NOT_FOUND = 'DRIFT_ANALYSIS_CAUSES_NOT_FOUND'
}

const waitForLoad = () => {
  if (!window.analytics) throw new Error('analytics not loaded');
  const promiseForReady = new Promise<void>(resolve => window.analytics.ready(resolve));
  // if an ad blocker is used, analytics.ready never returns
  return promiseWithTimeout(promiseForReady, SEGMENT_READY_TIMEOUT);
};

const tryInitPosthog = async () => {
  if (posthogStore.isInitialized()) return;
  if (!isProd || !posthogKey || !posthogHost) return;

  try {
    const posthog = await loadPosthog();
    posthog.init(posthogKey, {
      api_host: posthogHost,
      prepare_external_dependency_script: script => {
        script.nonce = window.nonce;
        return script;
      },
      prepare_external_dependency_stylesheet: stylesheet => {
        stylesheet.nonce = window.nonce;
        return stylesheet;
      }
    });
    posthogStore.setInitialized(true);
  } catch (e) {
    console.error('Failed to initialize PostHog', e);
  }
};

export const resetPosthog = () => {
  try {
    getPosthog()?.reset?.();
  } catch (e) {
    console.error('Failed to reset PostHog', e);
  } finally {
    posthogStore.reset();
  }
};

export const page = (name?: string, properties?: Record<string, any>) => {
  try {
    window.analytics?.page(name, properties);
  } catch (error) {
    console.warn('Failed to track page view', error);
  }
};

export const track = (name: string, properties?: any) => {
  try {
    window.analytics?.track(name, properties);
  } catch (error) {
    console.warn(`Failed to track event: ${name}`, error);
  }
};

export const identify = async (id: string, profile: any) => {
  try {
    await tryInitPosthog();

    window.analytics?.identify(id, profile);

    const posthog = getPosthog();
    posthog?.identify(id, profile);
    // We want to map the user to a company based on the email domain
    // see https://env0.slack.com/archives/CH9ND4Z6C/p1763561930413839
    const companyDomain = profile.email?.split('@')[1];
    if (companyDomain && posthog) {
      posthog.group(PosthogGroup.COMPANY, companyDomain, { name: companyDomain.split('.')[0], domain: companyDomain });
    }

    Sentry.setUser({ id, ...profile });
  } catch (error) {
    console.warn(`Failed to identify: ${id}`, error);
  }
};

// envzero.com adds its GA4 ids to links into the app, so GA4 can credit a sign-up to the visit that brought it
// (ENG-3394). Kept in storage because the Auth0 redirect drops the query string before the sign-up is known.
export const captureGaAttribution = () => {
  try {
    const searchParams = new URLSearchParams(window.location.search);
    const gaClientId = searchParams.get('ga_client_id');
    if (!gaClientId || !GA_CLIENT_ID_PATTERN.test(gaClientId)) return;

    const gaSessionId = searchParams.get('ga_session_id');
    const attribution: GaAttribution = { gaClientId };
    if (gaSessionId && GA_SESSION_ID_PATTERN.test(gaSessionId)) attribution.gaSessionId = gaSessionId;
    BrowserStorage.setItem(GA_ATTRIBUTION_KEY, attribution);
  } catch (error) {
    console.warn('Failed to capture GA attribution', error);
  }
};

export const takeGaAttribution = (): GaAttribution | undefined => {
  try {
    const attribution = BrowserStorage.getItem(GA_ATTRIBUTION_KEY) ?? undefined;
    BrowserStorage.removeItem(GA_ATTRIBUTION_KEY);
    return attribution;
  } catch (error) {
    console.warn('Failed to read GA attribution', error);
    return undefined;
  }
};

export const getAnonymousId = async () => {
  try {
    await waitForLoad();
    return window.analytics.user().anonymousId();
  } catch {
    return '';
  }
};

export const captureProductEvent = (event: string, properties?: Record<string, unknown>): void => {
  void (async () => {
    try {
      await tryInitPosthog();
      getPosthog()?.capture(event, properties);
    } catch (error) {
      console.warn(`Failed to capture product event: ${event}`, error);
    }
  })();
};
